<template>
  <section
    :id="data.id"
    class="content-visibility relative sm:mx-auto pl-4 sm:px-8 py-6 rounded-none sm:rounded-lg"
    :class="isMobile ? 'h-min-[350px]' : 'h-min-[345px]'"
    :style="{ backgroundColor: constantData.style.containerColor }"
  >
    <div class="widget-header w-full relative flex items-center mb-8">
      <div
        class="flex items-center relative w-auto"
        :style="{ backgroundColor: constantData.style.containerColor, color: constantData.style.headerTextColor }"
      >
        <OIcon :icon="`category/${constantData.style.icon}-blue`" svg-class="w-7 h-7" />
        <div class="block pl-1 flex-shrink-0 mr-1" :class="isMobile ? 'text-xl' : 'text-20'">{{ constantData.text.headerName }}</div>
      </div>
      <a
        :href="constantData.text.headerLink"
        class="absolute flex items-center"
        :class="isMobile ? 'right-3' : 'right-5'"
        :style="{ backgroundColor: constantData.style.containerColor, color: constantData.style.linkColor }"
      >
        <div class="pl-1 flex-shrink-0 font-normal" :class="isMobile ? 'text-md' : 'text-base'">{{ constantData.text.headerLinkName }}</div>
        <ORawIcon icon="homepage/arrow-circle-right" svg-class="w-4 h-4 mx-1" />
      </a>
    </div>
    <Swiper
      :settings="settings"
      :modules="[Navigation, Pagination]"
      :pagination="{ clickable: true, el: '.swiper-pagination' }"
      @swiper="onSwiper"
    >
      <SwiperSlide v-for="(item, index) in articles" :key="index">
        <SliderWidgetCard :article="item" :style="{ color: constantData.style.linkColor }" />
      </SwiperSlide>
      <template #container-end>
        <div class="swiper-pagination"></div>
      </template>
    </Swiper>
    <ORawIcon
      v-if="!isMobile"
      ref="prevNavBtn"
      icon="homepage/arrow-circle-right"
      :style="{ color: constantData.style.arrowColor }"
      svg-class="
        absolute
        left-0
        top-1/2
        -translate-y-1/2 -translate-x-1/2
        u-center
        w-12
        h-12
        rounded-full
        z-1
        cursor-pointer
        border-white border-3
        bg-white
        rotate-180
        text-xl"
    />
    <ORawIcon
      v-if="!isMobile"
      ref="nextNavBtn"
      icon="homepage/arrow-circle-right"
      :style="{ color: constantData.style.arrowColor }"
      svg-class="
        absolute
        right-0
        top-1/2
        -translate-y-1/2
        translate-x-1/2
        u-center
        cursor-pointer
        w-12
        h-12
        rounded-full
        border-white border-3
        bg-white
        z-1
        text-xl"
    />
  </section>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from '@nuxtjs/composition-api';
import { Navigation, Pagination } from 'swiper';
import Swiper from '@/modules/sliders/core/Swiper';
import SwiperSlide from '@/modules/sliders/core/SwiperSlide';
import { memoize } from 'lodash';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import { useDevice } from '~/composables/useDevice';
import SliderWidgetCard from '~/modules/card/templates/homepage/SliderWidgetCard.vue';
import { OIcon, ORawIcon } from '~/modules/common/components/ui';

export default defineComponent({
  components: { Swiper, SwiperSlide, SliderWidgetCard, OIcon, ORawIcon },
  props: {
    data: { type: Object },
  },

  setup(props) {
    const swiperRef = ref(null);
    const nextNavBtn = ref(null);
    const prevNavBtn = ref(null);
    const articles = ref(props.data.data);
    const { sendDataLayer } = useTracking();
    const impressionTimeout = ref(null);
    const sliderDataLayerGeneratorFn = (item, position) => {
      const list = `Anasayfa - ${props.data.title}`;
      return sendDataLayer(
        {
          item,
          list,
          position,
        },
        ['init', 'initOnLoad', 'click']
      );
    };
    const sliderDataLayerGenerator = memoize(sliderDataLayerGeneratorFn, (item, position) => `${item._id}_${position}`);

    onMounted(() => {
      for (let i = 0; i < (isMobile.value ? 1 : 4); i++) {
        const { initOnLoadEvent: dataLayerSendEvent } = sliderDataLayerGenerator(articles.value[i], i + 1);
        dataLayerSendEvent();
      }
    });
    const onSwiper = (swiper) => {
      swiperRef.value = swiper;
      swiper.on('slideChange', function () {
        clearTimeout(impressionTimeout.value);
        impressionTimeout.value = setTimeout(() => {
          const slideIndex = swiper.realIndex;
          for (let i = slideIndex; i < (isMobile.value ? slideIndex + 1 : slideIndex + 4); i++) {
            const { initEvent: dataLayerSendEvent } = sliderDataLayerGenerator(articles.value[i], i + 1);
            dataLayerSendEvent({ preventDuplicate: true });
          }
        }, 1000);
      });
      swiper.on('click', function () {
        const slideIndex = parseInt(swiper.clickedSlide.dataset.swiperSlideIndex);
        sliderDataLayerGenerator(articles.value[slideIndex], slideIndex + 1).clickEvent();
      });
    };
    const slideChange = (index) => {
      swiperRef.value?.slideToLoop(index);
    };

    // Temporary data. When data will come from backend, these constants will be changed
    const constantData = {
      style: {
        icon: props.data.icon,
        containerColor: '#080C25',
        arrowColor: '#080C25',
        linkColor: '#D0D9FF',
        headerTextColor: '#3071E7',
      },
      text: {
        headerName: props.data.title,
        headerLinkName: 'Tümünü Gör',
        headerLink: props.data.href,
      },
    };

    const { isMobile } = useDevice();
    const settings = computed(() => ({
      slidesPerView: isMobile.value ? 1.5 : 4,
      speed: 150,
      loop: true,
      slidesPerGroup: isMobile.value ? 1 : 4,
      preloadImages: false,
      navigation: {
        nextEl: nextNavBtn,
        prevEl: prevNavBtn,
      },
      breakpoints: {
        320: { spaceBetween: 16 },
        640: { spaceBetween: 24 },
      },
    }));

    return {
      settings,
      nextNavBtn,
      prevNavBtn,
      onSwiper,
      constantData,
      slideChange,
      Navigation,
      Pagination,
      isMobile,
      articles,
    };
  },
});
</script>
<style scoped lang="scss">
.widget-header::before {
  content: '';
  position: absolute;
  width: 100%;
  top: 50%;
  transform: translate(0, -50%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.22);
}
.content-visibility {
  content-visibility: auto;
  contain-intrinsic-size: 357px;
  @include sm() {
    content-visibility: visible;
    contain-intrinsic-size: 375px;
  }
}
</style>
